import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { money, type FormatSettings } from '../../../lib/format';
import type { ComboPick } from '../lib/cart';
import { dec, sum, toPlain } from '../lib/decimal';

export interface ComboGroupLike {
  id: string;
  name: string;
  items: { variantId: string; name: string; extraPrice: string; salePrice: string }[];
}

/**
 * Picks one item from each group of a combo before it goes on the order (POS and Fast sale).
 * A group with a single item is picked already.
 */
export function ComboPicker({
  name,
  price,
  groups,
  format,
  onPick,
  onClose,
}: {
  name: string;
  /** The combo's price per unit, before extras. */
  price: string;
  groups: ComboGroupLike[];
  format: FormatSettings;
  onPick: (picks: ComboPick[], price: string) => void;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      groups.flatMap((g) =>
        g.items.length === 1 && g.items[0] ? [[g.id, g.items[0].variantId]] : [],
      ),
    ),
  );
  const picks: ComboPick[] = groups.flatMap((g) => {
    const item = g.items.find((i) => i.variantId === chosen[g.id]);
    return item
      ? [
          {
            groupId: g.id,
            groupName: g.name,
            variantId: item.variantId,
            name: item.name,
            extraPrice: item.extraPrice,
            salePrice: item.salePrice,
          },
        ]
      : [];
  });
  const complete = groups.length > 0 && picks.length === groups.length;
  const total = toPlain(dec(price) + sum(picks.map((p) => dec(p.extraPrice))));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {name}
        <Typography variant="body2" color="text.secondary">
          Pick one from each group
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {groups.length === 0 ? (
          <Typography color="text.secondary">
            This combo has no choices set up yet. Add them on the product first.
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            {groups.map((g) => (
              <Box key={g.id} role="radiogroup" aria-label={g.name}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {g.name}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  }}
                >
                  {g.items.map((i) => {
                    const on = chosen[g.id] === i.variantId;
                    return (
                      <ButtonBase
                        key={i.variantId}
                        role="radio"
                        aria-checked={on}
                        onClick={() => setChosen((c) => ({ ...c, [g.id]: i.variantId }))}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          p: 1.5,
                          borderRadius: 2,
                          border: 2,
                          borderColor: on ? 'primary.main' : 'divider',
                          bgcolor: on ? 'action.selected' : 'background.paper',
                          minHeight: 64,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {i.name}
                          </Typography>
                          {dec(i.extraPrice) > 0n && (
                            <Typography variant="caption" color="text.secondary">
                              + {money(i.extraPrice, format)}
                            </Typography>
                          )}
                        </Box>
                        {on && <CheckCircleRounded color="primary" fontSize="small" />}
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography sx={{ flex: 1, fontWeight: 700 }} className="tabular">
          {money(total, format)}
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!complete} onClick={() => onPick(picks, total)}>
          Add to order
        </Button>
      </DialogActions>
    </Dialog>
  );
}
